import { ContextType } from '@dh-io-cscs/user-context-manager/dist';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import { FilterCode } from '../models/product-filter/product-filter-base.model';
import { SortCode } from '../models/sorting.model';

const defaultPriceSortCodesList: string[] = [
  SortCode.PriceAsc,
  SortCode.PriceDesc,
  SortCode.MonthlyAsc,
  SortCode.MonthlyDesc,
];

export const b2xSuffixRegex = /-b2(c|b)$/;

export const isB2xPriceSortCode = (code: string) => !!code.match(b2xSuffixRegex);
export const isDefaultPriceSortCode = (code: string) => defaultPriceSortCodesList.includes(code);
export const isPriceSortCode = (code: string) => isDefaultPriceSortCode(code) || isB2xPriceSortCode(code);

const defaultPriceFiltersList: string[] = [FilterCode.priceValue, FilterCode.monthlyRate];
const b2cPriceFiltersList: string[] = [FilterCode.b2cFilterPrice, FilterCode.b2cInstalmentRate];
const b2bPriceFiltersList: string[] = [FilterCode.b2bFilterPrice, FilterCode.b2bInstalmentRate];

export const isB2xPriceFilter = (code: FilterCode | string): boolean =>
  b2bPriceFiltersList.includes(code) || b2cPriceFiltersList.includes(code);
export const isDefaultPriceFilter = (code: FilterCode | string) => defaultPriceFiltersList.includes(code);
export const isPriceFilter = (code: FilterCode | string) => isB2xPriceFilter(code) || isDefaultPriceFilter(code);

// Mapping to map price filter codes to their b2x equivalent and vice versa
const priceFilterCodesMapping = {
  [FilterCode.priceValue]: {
    [CONTEXT_TYPES.B2C]: FilterCode.b2cFilterPrice,
    [CONTEXT_TYPES.B2B]: FilterCode.b2bFilterPrice,
    default: FilterCode.priceValue,
  },
  [FilterCode.monthlyRate]: {
    [CONTEXT_TYPES.B2C]: FilterCode.b2cInstalmentRate,
    [CONTEXT_TYPES.B2B]: FilterCode.b2bInstalmentRate,
    default: FilterCode.monthlyRate,
  },
  [FilterCode.b2cFilterPrice]: {
    [CONTEXT_TYPES.B2B]: FilterCode.b2bFilterPrice,
    [CONTEXT_TYPES.B2C]: FilterCode.b2cFilterPrice,
    default: FilterCode.priceValue,
  },
  [FilterCode.b2bFilterPrice]: {
    [CONTEXT_TYPES.B2B]: FilterCode.b2bFilterPrice,
    [CONTEXT_TYPES.B2C]: FilterCode.b2cFilterPrice,
    default: FilterCode.priceValue,
  },
  [FilterCode.b2cInstalmentRate]: {
    [CONTEXT_TYPES.B2B]: FilterCode.b2bInstalmentRate,
    [CONTEXT_TYPES.B2C]: FilterCode.b2cInstalmentRate,
    default: FilterCode.monthlyRate,
  },
  [FilterCode.b2bInstalmentRate]: {
    [CONTEXT_TYPES.B2B]: FilterCode.b2bInstalmentRate,
    [CONTEXT_TYPES.B2C]: FilterCode.b2cInstalmentRate,
    default: FilterCode.monthlyRate,
  },
};

export const dynamicPriceFilters = (userContext: ContextType | string, filter: { code: string }) => {
  if (filter.code === FilterCode.monthlyRate || filter.code === FilterCode.priceValue) {
    return false;
  }

  if (filter.code === FilterCode.b2bInstalmentRate || filter.code === FilterCode.b2bFilterPrice) {
    return userContext === CONTEXT_TYPES.B2B;
  }

  if (filter.code === FilterCode.b2cInstalmentRate || filter.code === FilterCode.b2cFilterPrice) {
    return userContext === CONTEXT_TYPES.B2C;
  }

  return true;
};

// Convert default price filter codes to b2x codes and vice versa
export const convertPriceFilterCode = (code: FilterCode | string, userContext?: CONTEXT_TYPES | string) => {
  if (!isPriceFilter(code)) {
    return code;
  }

  if (userContext) {
    return priceFilterCodesMapping[code][userContext];
  } else {
    return priceFilterCodesMapping[code].default;
  }
};

// Remove all opposite user context specific price sort options: if context is B2B remove all B2C sort options and vice versa
export const filterB2xSpecificPriceSortItems = (sortCode: string, userContext: CONTEXT_TYPES | string) => {
  // Filter out options without user context
  if (defaultPriceSortCodesList.includes(sortCode)) {
    return false;
    // Filter out opposite context options
  } else if (userContext === CONTEXT_TYPES.B2C && sortCode.endsWith('b2b')) {
    return false;
  } else if (userContext === CONTEXT_TYPES.B2B && sortCode.endsWith('b2c')) {
    return false;
  }

  return true;
};

// Convert defaulrt sort codes to b2x codes and vice versa
export const convertPriceSortCode = (code: string, userContext?: CONTEXT_TYPES | string) => {
  if (!isPriceSortCode(code)) {
    return code;
  }

  if (!userContext) {
    // Remove potential b2x suffix
    return code.replace(b2xSuffixRegex, '');
  } else if (!isB2xPriceSortCode(code)) {
    // Add current user context as suffix
    return `${code}-${userContext.toLowerCase()}`;
  } else if (!code.endsWith(userContext.toLowerCase())) {
    // Swap suffix with current one
    const oldSuffix = userContext === CONTEXT_TYPES.B2B ? 'b2c' : 'b2b';
    const newSuffix = userContext.toLowerCase();

    return code.replace(oldSuffix, newSuffix);
  } else {
    return code;
  }
};
